import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { auth } from '../../firebaseConfig';
import useUserStore from '../../stores/userStore';
import usePushNotificationStore from '../../stores/pushNotificationStore';
import api from '../../common/api/api';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import {
  CONNECT_AI_AUTHORIZE_PATH,
  CONNECT_AI_PENDING_REQUEST_KEY,
} from '../common/connect-ai-consent';
import { GHL_PENDING_INSTALL_CODE_KEY } from '../common/ghl-install';
import {
  clearPostLoginRedirect,
  peekPostLoginRedirect,
  postLoginPath,
  stashPostLoginRedirect,
} from '../common/post-login-redirect';

type AuthStatus = 'LOADING' | 'INITIALIZING' | 'SIGNED_IN' | 'SIGNED_OUT';

// Lazy initial value for `status`. On a client-side navigation the app is already
// initialized and Firebase still has the same user: start this mount straight at
// SIGNED_IN so withAuth never renders its spinner while onIdTokenChanged (below) catches
// up asynchronously -- that async gap is what made heavier pages flash the spinner the
// longest. First load, SSR/hydration (no `window` yet, so this also avoids a hydration
// mismatch), and an account switch (different uid) still start LOADING and run the
// listener's normal init/refresh path.
const initialStatus = (): AuthStatus => {
  if (typeof window === 'undefined') return 'LOADING';
  const store = useUserStore.getState();
  const { currentUser } = auth;
  if (store.isInitialized && currentUser && currentUser.uid === store.user?.uid) {
    return 'SIGNED_IN';
  }
  return 'LOADING';
};

export const useAuth = () => {
  const [status, setStatus] = useState<AuthStatus>(() => initialStatus());
  const router = useRouter();
  // True once this tab has seen a session, so a sign-out isn't mistaken for an arriving link.
  const hadSessionRef = useRef(false);
  // True once this mount ran the blocking init; later token events refresh silently.
  const handledFirstEventRef = useRef(false);
  const { 
    setUserData, 
    organizations,
    isInitialized,
    setOrganizations,
    setIsInitialized,
  } = useUserStore();

  // 1. Manejo de cambios de sesión
  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(async (authUser: any) => {
      if (!authUser) {
        handledFirstEventRef.current = false;
        setStatus('SIGNED_OUT');
        setOrganizations([]);
        setIsInitialized(false);
        // Someone arriving from their AI client has no session yet. Stash the signed request
        // before bouncing to login, or replace() drops it and the whole flow dies silently.
        if (typeof window !== 'undefined'
          && window.location.pathname === CONNECT_AI_AUTHORIZE_PATH) {
          try {
            const pending = new URLSearchParams(window.location.search).get('request');
            if (pending) window.sessionStorage.setItem(CONNECT_AI_PENDING_REQUEST_KEY, pending);
          } catch {
            // Storage blocked: the consent flow won't survive the login round trip, but the
            // sign-out redirect below still has to happen for every other page in the app.
          }
        }
        // A conversation link opened without a session: resume it after login. Skipped after a
        // sign-out, or the next person to log in on this tab would land in that chat.
        if (typeof window !== 'undefined' && !hadSessionRef.current) {
          stashPostLoginRedirect(window.location.pathname + window.location.search);
        }
        if (!['/login', '/loginpass'].includes(router.pathname)) {
          router.replace('/login');
        }
        return;
      }

      hadSessionRef.current = true;
      try {
        const token = await authUser.getIdToken();
        api.setToken(token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        usePushNotificationStore.getState().syncToken(authUser.uid);

        const userPayload = {
          uid: authUser.uid,
          displayName: authUser.displayName,
          phoneNumber: authUser.phoneNumber,
          accessToken: token,
          email: authUser.email,
        };

        // This listener closes over the first render, so it can't read `isInitialized` or
        // `status`. Only the first event of a mount runs the blocking init: later ones are
        // token refreshes (hourly, or forced), and INITIALIZING there made withAuth swap the
        // page for a spinner, unmounting it and dropping drafts and unsaved forms.
        if (!handledFirstEventRef.current) {
          handledFirstEventRef.current = true;
          // Read live, not from the closure: the store may have been initialized by an
          // earlier page in this session. If so, and for this same user, this mount is a
          // plain navigation, not a first load — go straight to SIGNED_IN with no spinner and
          // no refetch, same as production. The token was already swapped above. A different
          // uid (an account switch with no sign-out in between) still runs the full init.
          const store = useUserStore.getState();
          if (store.isInitialized && store.user?.uid === authUser.uid) {
            setStatus('SIGNED_IN');
          } else {
            setStatus('INITIALIZING');
            await setUserData(userPayload);
            setStatus('SIGNED_IN');
          }
        } else {
          setStatus('SIGNED_IN');
          // Not awaited: keeps role, orgs and credits fresh without blocking the page.
          setUserData(userPayload);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            hook: 'useAuth',
            phase: 'onIdTokenChanged',
            pathname: router.pathname,
            // uid only: serializing the Firebase user leaked its refresh token into Sentry.
            user_id: authUser.uid,
            // Read live: the closure's `isInitialized` and `status` are frozen at first render.
            is_initialized: String(useUserStore.getState().isInitialized),
            is_refresh: String(handledFirstEventRef.current),
          },
        });
        console.error('Error setting up authenticated user:', error);
        handledFirstEventRef.current = false;
        setStatus('SIGNED_OUT');
      }
    });

    return () => unsubscribe();
  }, []);

  // 2. Manejo de redirecciones
  useEffect(() => {
    const hasOrgs = organizations.length > 0;

    if (status === 'SIGNED_IN' && isInitialized) {
      // Resume a GHL marketplace install that was interrupted by login.
      const pendingGhlCode = typeof window !== 'undefined'
        ? window.localStorage.getItem(GHL_PENDING_INSTALL_CODE_KEY)
        : null;
      if (pendingGhlCode && hasOrgs
        && router.pathname !== '/integrations/crm/install') {
        router.replace('/integrations/crm/install');
        return;
      }

      // Same idea for a consent flow interrupted by login: send them back to finish it.
      // Guarded: this is the app-wide redirect effect, so a storage exception here would break
      // routing for every signed-in user, not just the ones coming from an AI client.
      let pendingConsent: string | null = null;
      try {
        pendingConsent = typeof window !== 'undefined'
          ? window.sessionStorage.getItem(CONNECT_AI_PENDING_REQUEST_KEY)
          : null;
      } catch {
        // No stash readable: fall through to the ordinary redirects below.
      }
      if (pendingConsent && hasOrgs && router.pathname !== CONNECT_AI_AUTHORIZE_PATH) {
        try {
          window.sessionStorage.removeItem(CONNECT_AI_PENDING_REQUEST_KEY);
        } catch {
          // The consent page clears it too, so a failure here is not what decides the flow.
        }
        router.replace(
          `${CONNECT_AI_AUTHORIZE_PATH}?request=${encodeURIComponent(pendingConsent)}`,
        );
        return;
      }

      if (hasOrgs) {
        // si ya está logueado con organizaciones y está en /login o /subscribe => envía al enlace
        // pendiente (una conversación abierta sin sesión) o al dashboard
        if (['/login', '/loginpass', '/subscribe'].includes(router.pathname)) {
          router.replace(postLoginPath());
        } else if (peekPostLoginRedirect()) {
          // Already past login: the pending link was followed (or superseded), so drop it.
          clearPostLoginRedirect();
        }
      } else {
        // si NO hay organizaciones y no está en /subscribe => envía a /subscribe
        if (router.pathname !== '/subscribe') {
          router.replace('/subscribe');
        }
      }
    }
  }, [status, isInitialized, organizations.length, router.pathname]);

  return {
    status,
    isAuthenticated: status === 'SIGNED_IN',
    isLoading: status === 'LOADING' || status === 'INITIALIZING',
    hasOrganizations: organizations.length > 0,
    isInitialized,
    updateStatus: (newStatus: AuthStatus) => setStatus(newStatus)
  };
};